import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEO from '@/components/feature/SEO';

const aboutJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '汗汗漫画',
  url: 'https://hanhancomic.com',
  description: '汗汗漫画主打下拉式漫画连续阅读，滚动观看不间断，漫画下拉即可加载全集，免费流畅观看各类漫画，感受全新阅读方式。',
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    email: 'contact@hanhancomic.com',
    availableLanguage: ['Chinese'],
  },
};

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '汗汗漫画是什么？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '汗汗漫画是一个主打下拉式漫画连续阅读的在线漫画平台。用户只需向下滚动页面，即可连续加载漫画内容，无需手动翻页，享受流畅丝滑的阅读体验。',
      },
    },
    {
      '@type': 'Question',
      name: '汗汗漫画是完全免费的吗？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '是的，汗汗漫画上的所有漫画内容均免费提供，用户可以无限制地浏览和阅读各类漫画作品。',
      },
    },
    {
      '@type': 'Question',
      name: '如何在汗汗漫画上阅读漫画？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '访问汗汗漫画网站后，您可以通过首页的分类导航或搜索功能找到喜欢的漫画，点击进入后向下滚动即可开始下拉式连续阅读。',
      },
    },
    {
      '@type': 'Question',
      name: '汗汗漫画支持哪些设备？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '汗汗漫画完美适配电脑、平板和手机等各类设备，无论您在哪种设备上访问，都能获得最佳的阅读体验。',
      },
    },
    {
      '@type': 'Question',
      name: '如何联系汗汗漫画的客服？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '您可以通过发送邮件至 contact@hanhancomic.com 联系我们的客服团队。我们的在线客服工作时间为周一至周五 9:00-18:00。',
      },
    },
  ],
};

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const urlEncoded = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'website_alt') {
          urlEncoded.append(key, value as string);
        }
      });

      const response = await fetch(form.action, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlEncoded.toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: '关于我们' },
  ];

  return (
    <>
      <SEO
        title="关于我们 - 汗汗漫画 | 下拉式漫画连续阅读平台"
        description="汗汗漫画品牌介绍 - 主打下拉式漫画连续阅读体验，滚动观看不间断，漫画下拉即可加载全集。了解我们的品牌故事、用户口碑和联系方式。"
        keywords="汗汗漫画,关于我们,品牌介绍,联系方式,下拉式漫画,在线阅读"
        canonicalUrl="https://hanhancomic.com/about"
        jsonLd={{ ...aboutJsonLd, ...faqJsonLd }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-16">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="mb-6">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            <section className="mb-16">
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-6">
                关于汗汗漫画
              </h1>
              <div className="prose prose-sm max-w-none text-foreground-600 leading-relaxed space-y-4">
                <p>
                  汗汗漫画成立于2024年，是一个专注于<strong>下拉式漫画连续阅读</strong>体验的在线漫画平台。我们致力于为用户提供最流畅、最便捷的漫画阅读方式，让漫画爱好者能够沉浸在无缝的阅读体验中。
                </p>
                <p>
                  传统的漫画阅读方式需要频繁点击翻页，这不仅打断了阅读的连贯性，也影响了读者的沉浸感。汗汗漫画创新性地采用了<strong>下拉滚动式阅读</strong>技术——只需向下滚动页面，漫画内容即可自动连续加载，全集一气呵成，无需任何手动翻页操作。
                </p>
                <p>
                  我们的平台汇集了<strong>热血、恋爱、搞笑、奇幻、悬疑、日常、冒险、科幻</strong>等多个分类的优质漫画作品，覆盖了广泛的题材和风格，满足不同读者的口味需求。无论你是喜欢燃爆的战斗场面，还是偏爱温馨的日常故事，都能在汗汗漫画找到属于你的那一部。
                </p>
              </div>
            </section>

            <section className="mb-16">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-6">
                核心优势
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-6 bg-background-100 rounded-lg border border-background-200/60">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
                    <i className="ri-scroll-to-bottom-line text-xl"></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">下拉式连续阅读</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    创新的下拉滚动阅读方式，告别传统翻页。向下滚动即可自动加载后续内容，阅读体验行云流水，绝无中断。
                  </p>
                </div>
                <div className="p-6 bg-background-100 rounded-lg border border-background-200/60">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                    <i className="ri-flashlight-line text-xl"></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">极速加载技术</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    采用先进的图片预加载和懒加载技术，确保每一页漫画都能秒开显示，绝不因加载速度影响阅读体验。
                  </p>
                </div>
                <div className="p-6 bg-background-100 rounded-lg border border-background-200/60">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 mb-4">
                    <i className="ri-smartphone-line text-xl"></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">全平台完美适配</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    无论是桌面电脑、平板还是手机，汗汗漫画都能提供完美适配的阅读界面，随时随地畅享阅读乐趣。
                  </p>
                </div>
                <div className="p-6 bg-background-100 rounded-lg border border-background-200/60">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
                    <i className="ri-coin-line text-xl"></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">完全免费阅读</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    平台上所有漫画内容均免费提供，无需付费订阅即可畅读所有作品，让每一位漫画爱好者都能无门槛享受阅读。
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-16">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-6">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div className="p-5 bg-background-100 rounded-lg border border-background-200/60">
                  <div className="flex items-center gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <i key={s} className="ri-star-fill text-accent-500 text-sm w-4 h-4 flex items-center justify-center"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    下拉式阅读真的太方便了！以前看漫画要点来点去翻页很烦，现在直接往下滑就行，完全沉浸在故事里了。
                  </p>
                  <p className="text-xs text-foreground-500 font-medium">—— 漫画爱好者小王</p>
                </div>
                <div className="p-5 bg-background-100 rounded-lg border border-background-200/60">
                  <div className="flex items-center gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <i key={s} className="ri-star-fill text-accent-500 text-sm w-4 h-4 flex items-center justify-center"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    上班摸鱼用手机看很方便，加载速度也很快。漫画种类也挺多的，各种类型都有，目前最喜欢的漫画平台。
                  </p>
                  <p className="text-xs text-foreground-500 font-medium">—— 上班族小李</p>
                </div>
                <div className="p-5 bg-background-100 rounded-lg border border-background-200/60">
                  <div className="flex items-center gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <i key={s} className="ri-star-fill text-accent-500 text-sm w-4 h-4 flex items-center justify-center"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    页面设计简洁大方，没有乱七八糟的广告弹窗。下拉式阅读的体验比传统翻页漫画网站好太多了！
                  </p>
                  <p className="text-xs text-foreground-500 font-medium">—— 学生党小陈</p>
                </div>
              </div>
            </section>

            <section className="mb-16">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-6">
                常见问题
              </h2>
              <div className="space-y-4">
                {[
                  { q: '汗汗漫画是什么？', a: '汗汗漫画是一个主打下拉式漫画连续阅读的在线漫画平台。用户只需向下滚动页面，即可连续加载漫画内容，无需手动翻页，享受流畅丝滑的阅读体验。' },
                  { q: '汗汗漫画是完全免费的吗？', a: '是的，汗汗漫画上的所有漫画内容均免费提供，用户可以无限制地浏览和阅读各类漫画作品。' },
                  { q: '如何在汗汗漫画上阅读漫画？', a: '访问汗汗漫画网站后，您可以通过首页的分类导航或搜索功能找到喜欢的漫画，点击进入后向下滚动即可开始下拉式连续阅读。' },
                  { q: '汗汗漫画支持哪些设备？', a: '汗汗漫画完美适配电脑、平板和手机等各类设备，无论您在哪种设备上访问，都能获得最佳的阅读体验。' },
                  { q: '如何联系汗汗漫画的客服？', a: '您可以通过发送邮件至 contact@hanhancomic.com 联系我们的客服团队。我们的在线客服工作时间为周一至周五 9:00-18:00。' },
                ].map((faq, idx) => (
                  <details key={idx} className="group bg-background-100 rounded-lg border border-background-200/60">
                    <summary className="px-5 py-4 cursor-pointer flex items-center justify-between text-sm font-semibold text-foreground-900">
                      {faq.q}
                      <i className="ri-add-line group-open:hidden w-5 h-5 flex items-center justify-center text-foreground-400"></i>
                      <i className="ri-subtract-line hidden group-open:block w-5 h-5 flex items-center justify-center text-foreground-400"></i>
                    </summary>
                    <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                      {faq.a}
                    </div>
                  </details>
                ))}
              </div>
            </section>

            <section className="mb-16">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-6">
                联系方式
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="p-6 bg-background-100 rounded-lg border border-background-200/60">
                  <h3 className="text-base font-semibold text-foreground-900 mb-4">联系信息</h3>
                  <ul className="space-y-3 text-sm text-foreground-600">
                    <li className="flex items-start gap-3">
                      <i className="ri-mail-line text-primary-500 mt-0.5 w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                      <div>
                        <p className="font-medium text-foreground-900">电子邮箱</p>
                        <p>contact@hanhancomic.com</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-customer-service-2-line text-primary-500 mt-0.5 w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                      <div>
                        <p className="font-medium text-foreground-900">在线客服</p>
                        <p>周一至周五 9:00 - 18:00</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-time-line text-primary-500 mt-0.5 w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                      <div>
                        <p className="font-medium text-foreground-900">响应时间</p>
                        <p>我们会在24小时内回复您的邮件</p>
                      </div>
                    </li>
                  </ul>
                </div>

                <div className="p-6 bg-background-100 rounded-lg border border-background-200/60">
                  <h3 className="text-base font-semibold text-foreground-900 mb-4">给我们留言</h3>
                  <form
                    data-readdy-form="true"
                    action="https://readdy.ai/api/form/d9omurqqs38sj6jc7bng"
                    method="POST"
                    encType="application/x-www-form-urlencoded"
                    className="space-y-4"
                    onSubmit={handleSubmit}
                  >
                    <div>
                      <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1">
                        姓名
                      </label>
                      <input
                        type="text"
                        id="about-name"
                        name="name"
                        required
                        className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all text-foreground-800"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1">
                        邮箱
                      </label>
                      <input
                        type="email"
                        id="about-email"
                        name="email"
                        required
                        className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all text-foreground-800"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1">
                        留言内容
                      </label>
                      <textarea
                        id="about-message"
                        name="message"
                        required
                        rows={4}
                        maxLength={500}
                        className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all text-foreground-800 resize-none"
                        placeholder="请输入您的留言内容（最多500字）"
                      ></textarea>
                    </div>
                    <div className="spam-guard">
                      <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                    </div>
                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="w-full px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {formStatus === 'submitting' ? '发送中...' : '发送留言'}
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-sm text-green-600 text-center mt-2">留言发送成功！我们会尽快回复您。</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-500 text-center mt-2">{formError}</p>
                    )}
                  </form>
                </div>
              </div>
            </section>
          </div>
        </main>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}